import { Id } from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCompaniesContext } from 'company/context'
import { showCompanyHeaderDropdown } from 'company/dropdown'
import { ManagementTeamScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { useAddEditUserContext } from './context'
import { useManagementTeamActions } from './management_actions_context'
import { useUserList } from './queries/list'
import {
  USER_ROLE_DROPDOWN_TYPES,
  getUserRoleName,
  showUserRoleHeaderDropdown,
} from './user_role_dropdown'
import { ManagementTeamView } from './views'

export function ManagementTeamScreen({
  navigation,
  route,
}: ManagementTeamScreenProps) {
  const { handleNavigation } = useRoutingContext()
  const { setRole, role } = useAddEditUserContext()
  const { companies, setActiveCompanyId, activeCompany } = useCompaniesContext()

  const { data: user } = useUser()
  const [selectedRoleIndex, setSelectedRoleIndex] = useState<number>(
    USER_ROLE_DROPDOWN_TYPES.indexOf(role ?? 'observer')
  )
  const { tempUsers } = useManagementTeamActions()
  const userListQuery = useUserList(
    activeCompany?.id,
    USER_ROLE_DROPDOWN_TYPES[selectedRoleIndex]
  )

  useEffect(() => {
    setRole(USER_ROLE_DROPDOWN_TYPES[selectedRoleIndex])
  }, [selectedRoleIndex, setRole])

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      getUserRoleName(USER_ROLE_DROPDOWN_TYPES[selectedRoleIndex])
    )
  }, [navigation, selectedRoleIndex])

  useLayoutEffect(() => {
    showUserRoleHeaderDropdown(
      navigation,
      selectedRoleIndex,
      setSelectedRoleIndex
    )
  }, [navigation, selectedRoleIndex])

  useLayoutEffect(() => {
    if (companies && companies.length > 1) {
      showCompanyHeaderDropdown(navigation, {
        activeCompanyId: activeCompany?.id,
        companies,
        setActiveCompanyId,
      })
    }
  }, [activeCompany?.id, companies, navigation, setActiveCompanyId])

  const onPressUser = useCallback(
    (userId: Id) => {
      if (tempUsers.map(tempUser => tempUser.id).includes(userId)) {
        errorAlert(
          new Error(t('userBeingAddedDescription')),
          t('userBeingAddedTitle')
        )
      } else {
        handleNavigation({
          routeName: 'team_member',
          routeParams: {
            userId: userId,
            role: USER_ROLE_DROPDOWN_TYPES[selectedRoleIndex],
          },
        })
      }
    },
    [handleNavigation, selectedRoleIndex, tempUsers]
  )

  const onAddUser = useCallback(() => {
    handleNavigation({ routeName: 'new_user_phone_number' })
  }, [handleNavigation])

  const onPressRecentChanges = useCallback(() => {
    handleNavigation({ routeName: 'member_changes' })
  }, [handleNavigation])

  const onRefresh = useCallback(async () => {
    await userListQuery.refetch()
  }, [userListQuery])

  const displayedUsers = useMemo(
    () =>
      userListQuery.data?.map(observer =>
        observer.id === user?.id
          ? {
              ...observer,
              lastName: `${observer.lastName ?? ''} (${t('you')})`,
            }
          : observer
      ),
    [user?.id, userListQuery.data]
  )

  return (
    <StatusBoundary
      isLoading={userListQuery.isLoading}
      error={userListQuery.error}
    >
      <ManagementTeamView
        company={activeCompany}
        role={role}
        users={displayedUsers}
        tempUsers={tempUsers}
        onPressUser={onPressUser}
        onAddUser={onAddUser}
        onPressRecentChanges={onPressRecentChanges}
        onRefresh={onRefresh}
        lastUpdatedAt={userListQuery.dataUpdatedAt}
        isRefreshing={userListQuery.isFetching}
      />
    </StatusBoundary>
  )
}
